import { Inject, inject, Injectable, Injector, Optional } from '@angular/core';
import { AppModules } from '@common-modules/shared/app-modules.enum';
import { BaseService } from '@common-modules/shared/base.service';
import { ChartHelperService } from '@common-modules/shared/charts/chart-helper.service';
import { FiltrableItemManagerService } from '@common-modules/shared/charts/filtrable-item-manager.service';
import { AxisLocation } from '@common-modules/shared/charts/model/axes/axis-location';
import {
  HorizontalAxis,
  IHorizontalAxis,
} from '@common-modules/shared/charts/model/axes/horizontal-axis';
import {
  IVerticalAxis
} from '@common-modules/shared/charts/model/axes/vertical-axis';
import { ChartConfiguration } from '@common-modules/shared/charts/model/chart/chart-configuration';
import { ChartCustomization } from '@common-modules/shared/charts/model/chart/chart-customization';
import {
  IPlotableSerie
} from '@common-modules/shared/charts/model/series/plotable-serie';
import { GlobalDisableSettings } from '@common-modules/shared/config/global-disable-settings';
import { GLOBAL_DISABLE_SETTINGS } from '@common-modules/shared/core/injection-tokens/global-disable-settings.token';
import { ArrayHelperService } from '@common-modules/shared/helpers/array-helper.service';
import { DateHelperService } from '@common-modules/shared/helpers/date-helper.service';
import { LocalizationHelperService } from '@common-modules/shared/localization/localization-helper.service';
import { EventsControlSettings } from '@common-modules/shared/model/data-viz/events-control-settings.dto';
import { IFiltrableItemDto } from '@common-modules/shared/model/filtrable-items/filtrable-item.dto';
import { FiltrableItemTypeEnum } from '@common-modules/shared/model/filtrable-items/types/filtrable-item-type-enum';
import { IFiltrableType } from '@common-modules/shared/model/filtrable-items/types/filtrable-type';
import { UnitTypeConversionViewDto } from '@common-modules/shared/model/uom/unit-type-conversion-view.dto';
import { chartUtilsHelper } from '@water-loss/features/shared/helpers/chart-utils-helpers';
import { AlgorithmFiltrableType } from '@water-loss/features/shared/model/filtrable-items/types/algorithm-filtrable-type';
import { plainToInstance } from 'class-transformer';
import { asEnumerable } from 'linq-es2015';
import { forkJoin, map, Observable, take } from 'rxjs';
import { EventViewCategory } from '../events-chart/models/event-view-category';
import { EventChartQueryDto } from '../events-chart/models/events-chart-query.dto';
import { ChartSerieType } from '../models/chart-serie-type';
import { ChartSerieTypeEnum } from '../models/chart-serie-type.enum';

@Injectable({
  providedIn: 'root',
})
export abstract class BaseChartConfigurationService extends BaseService {
  // expiration: GetCacheOpts = { avoid: true };

  private readonly _chartDefaultsKey = `${AppModules.WlmCharts}.defaults`;
  protected readonly _naUnitTypeId = 50;
  private readonly _arrayHelperService = inject(ArrayHelperService);
  private readonly _dateHelperService = inject(DateHelperService);
  private readonly _localizationHelperService = inject(LocalizationHelperService);
  protected readonly _chartHelperService = inject(ChartHelperService);
  private readonly _filtrableItemManagerService = inject(FiltrableItemManagerService);

  constructor(
    injector: Injector,
    @Inject(GLOBAL_DISABLE_SETTINGS)
    @Optional()
    private _globalDisableSettings?: GlobalDisableSettings,
  ) {
    super(injector);
  }

  getChartConfigurationFromFiltrableItems(
    filtrableItems: IFiltrableItemDto[],
  ): Observable<ChartConfiguration> {
    const minStartDate = this._arrayHelperService.sortObjects<IFiltrableItemDto>(
      filtrableItems.slice(),
      (i) => new Date(i.startDate).getTime(),
      true,
    )?.[0].startDate;

    let maxEndDate = this._arrayHelperService.sortObjects<IFiltrableItemDto>(
      filtrableItems.slice(),
      (i) => new Date(i.endDate).getTime(),
      false,
    )?.[0].endDate;

    maxEndDate = this._dateHelperService.addDays(new Date(maxEndDate), 1);

    const eventsControlSettings = this.getDefaultEventsControlSettings();
    const eventsQuery = this.getEventQuery(filtrableItems, eventsControlSettings);

    const defaultLabels$ = this._localizationHelperService.get(this._chartDefaultsKey);
    const uomByFiltrableItems$ = filtrableItems.map((i) => this.getUomFromFiltrableItem(i));

    return forkJoin([defaultLabels$, ...uomByFiltrableItems$]).pipe(
      take(1),
      map((result) => {
        const [defaultLabels, ...uomByFiltrableItems] = result;

        const itemsUomConversions = uomByFiltrableItems.reduce((res, obj) => {
          Object.entries(obj).forEach(([key, value]) => {
            res.set(key, value);
          });
          return res;
        }, new Map<string, UnitTypeConversionViewDto>());

        const customization = this.getDefaultCustomization(defaultLabels);
        const horizontalAxis = this.getDefaultHorizontalAxis(minStartDate, maxEndDate);
        const verticalAxis = this.getDefaultVerticalAxis(itemsUomConversions);
        const seriesConfiguration = this.getDefaultPlotableSerie(
          filtrableItems,
          itemsUomConversions,
          verticalAxis,
          horizontalAxis[0]?.id,
        );

        const configData = {
          eventsControlSettings,
          horizontalAxis,
          verticalAxis: Array.from(verticalAxis.values()),
          seriesConfiguration,
          customization,
          eventsQuery,
        };

        return plainToInstance(ChartConfiguration, configData, {
          enableImplicitConversion: true
        });
      }),
    );
  }

  getChartSerieTypes(): ChartSerieType[] {
    const T_SCOPE_SERIE_TYPE = `${AppModules.WlmCharts}.serie-types`;
    const serieTypes = [];

    Object.entries(ChartSerieTypeEnum).forEach(([key, value]) =>
      serieTypes.push(
        new ChartSerieType({
          value: ChartSerieTypeEnum[key],
          labelKey: `${T_SCOPE_SERIE_TYPE}.${value}`,
        }),
      ),
    );

    return serieTypes;
  }

  abstract getEventCategories(): EventViewCategory[];

  abstract getDefaultVerticalAxis(
    itemsUomConversions: Map<string, UnitTypeConversionViewDto>,
  ): Map<number, IVerticalAxis>;

  private getDefaultHorizontalAxis(startDate: Date, endDate: Date): IHorizontalAxis[] {
    const defaultAxis = {
      id: 0,
      maximum: endDate,
      minimum: startDate,
      location: AxisLocation.Bottom,
    }
    return [plainToInstance(HorizontalAxis, defaultAxis, {
      enableImplicitConversion: true
    })];
  }

  abstract getDefaultPlotableSerie(
    filtrableItems: IFiltrableItemDto[],
    itemsConversions: Map<string, UnitTypeConversionViewDto>,
    verticalAxis: Map<number, IVerticalAxis>,
    horizontalAxisId: number,
  ): IPlotableSerie[];


  protected showInUtc(filtrableType: IFiltrableType): boolean {
    switch (filtrableType.type) {
      case FiltrableItemTypeEnum.Algorithm:
        const { algorithmShortName, timeAggregationId } = filtrableType as AlgorithmFiltrableType;
        return chartUtilsHelper.showInUtc(algorithmShortName, timeAggregationId);

      case FiltrableItemTypeEnum.Signal:
      default:
        return false;
    }
  }

  protected showAlwaysInTooltip(filtrableType: IFiltrableType): boolean {
    switch (filtrableType.type) {
      case FiltrableItemTypeEnum.Algorithm:
        const { timeAggregationId } = filtrableType as AlgorithmFiltrableType;

        return chartUtilsHelper.showAlwaysInTooltip(timeAggregationId);

      case FiltrableItemTypeEnum.Signal:
      default:
        return false;
    }
  }

  private getDefaultCustomization(defaultLabels: any): ChartCustomization {
    return new ChartCustomization({
      name: defaultLabels?.name ?? null,
      title: defaultLabels?.title ?? null,
      showLabels: false,
      showLegend: true,
      showTimeBar: true,
      isEventChartExpanded: false,
      isPopup: false,
      showChart: true,
      timeBarMinSelectionRange: 1,
      timeBarSelectedPeriodMode: 2,
      timeBarVisiblePeriodMode: 3,
    });
  }

  abstract getDefaultEventsControlSettings(): EventsControlSettings;

  private getEventQuery(
    filtrableItems: IFiltrableItemDto[],
    eventChartSetting: EventsControlSettings,
  ): EventChartQueryDto {
    if (!eventChartSetting.categories?.length || this._globalDisableSettings?.disableEventChart) {
      return null;
    }

    const eventQueries = filtrableItems.map((i) => {
      const service = this._filtrableItemManagerService.getService(i.filtrableType.type);
      return service.getEventQuery(i) as EventChartQueryDto;
    });

    const iterableEventQueryes = asEnumerable(eventQueries);

    const itemsWithElements = iterableEventQueryes.Where((x) => x?.elementsIds?.length > 0);
    const itemsWithSignals = iterableEventQueryes.Where((x) => x?.signalIds?.length > 0);

    const maxEndDate = this._dateHelperService.getMaxDate(eventQueries.map((x) => x.endDate));

    const eventChartQuery = new EventChartQueryDto({
      endDate: this._dateHelperService.addDays(new Date(maxEndDate), 1),
      startDate: this._dateHelperService.getMinDate(eventQueries.map((x) => x.startDate)),
      elementsIds: itemsWithElements.Any()
        ? itemsWithElements.SelectMany((x) => x.elementsIds).ToArray()
        : null,
      signalIds: itemsWithSignals.Any()
        ? itemsWithSignals.SelectMany((x) => x?.signalIds).ToArray()
        : null,
      categories: eventChartSetting.categories,
    });

    return eventChartQuery;
  }

  abstract getUomFromFiltrableItem(
    item: IFiltrableItemDto,
  ): Observable<{ [id: string]: UnitTypeConversionViewDto }>;
}
